import { MultiselectOption } from "../../components/multiselect/types";
import {
  AdvancedSearchFilterData,
  AdvancedSearchQuery,
  AdvancedSearchRowData,
  advancedSearchFilters,
  AdvancedSearchTextInputFilterData
} from "./types";

const getSpace = (currentText: string) => {
  let space = "";
  switch (currentText.trim()) {
    case "":
      space = "";
      break;
    default:
      if (currentText.trim().endsWith("=")) {
        space = "";
        break;
      }
      space = " ";
  }
  return space;
};

const translateRowsToCql = (rowsToTranslate: AdvancedSearchRowData[]) => {
  return rowsToTranslate.reduce((acc: string, curr: AdvancedSearchRowData) => {
    let rowTranslation = "";
    if (acc !== "" && curr.term.trim() !== "") {
      rowTranslation = rowTranslation.concat(" ", curr.clause.value);
    }
    if (curr.searchIndex !== "all" && curr.term.trim() !== "") {
      rowTranslation = rowTranslation.concat(" ", curr.searchIndex, "=");
    }
    if (curr.term !== "") {
      rowTranslation = rowTranslation.concat(
        getSpace(acc),
        "'",
        curr.term,
        "'"
      );
    }
    return acc + rowTranslation;
  }, "");
};

// N.B! if using MultiselectWithInputs, 
// the input parts are handled further down in
// translateTextInputFiltersToCql
const translateFilterToCql = (
  filterToTranslate: MultiselectOption[],
  cqlKey: keyof typeof advancedSearchFilters
) => {
  let translation = filterToTranslate.reduce(
    (acc: string, curr: MultiselectOption) => {
      let filterTranslation = "";
      const relation = acc.trim() === "" ? " AND" : " OR";  
      if (curr.value === "all") {
        return `${acc}`;
      }
      // const operator = cqlKey === "ageGroups" ? " within " : "=";
      const operator = ["ageGroups", "publicationYear"].includes(cqlKey) ? " within " : "=";

      // The materialtype dropdown mixes general and specific types so switch cqlKey for certain options
      const specific = ["musik (cd)", "musik (grammofonplade)"];
      const finalCqlKey = specific.includes(curr.value) ? "specificmaterialtype" : cqlKey;
      
      filterTranslation = filterTranslation.concat(
        relation,
        ` ${advancedSearchFilters[finalCqlKey]}${operator}`,
        `'${curr.value}'`
      );
      return acc + filterTranslation;
    },
    ""
  );

  // // If there are no valid row terms, do not prepend 'AND'
  // if (!hasValidRowTerms) {
  //   return translation; // Return the translation without 'AND'
  // }

  // If multiple values are selected in a single filter, we need to wrap them in
  // parentheses & add move the opening AND clause before the parenthesis opening.
  if (filterToTranslate.length > 1) {
    translation = ` AND (${translation.split(" AND")[1]})`;
  }
  return translation;
};

const translateFiltersToCql = (
  filtersToTranslate: AdvancedSearchFilterData
) => {
  const filtersAsArray: MultiselectOption[][] = Object.keys(
    filtersToTranslate
  ).map((key) => filtersToTranslate[key as keyof AdvancedSearchFilterData]);

  const translatedFilters = filtersAsArray.reduce(
    (acc: string, curr: MultiselectOption[], index) => {
      return (
        acc +
        translateFilterToCql(
          curr,
          Object.keys(filtersToTranslate)[
            index
          ] as keyof typeof advancedSearchFilters
        )
      );
    },
    ""
  );
  return translatedFilters;
};

const translateTextInputFiltersToCql = (
  filtersToTranslate: AdvancedSearchTextInputFilterData,
  // hasValidRowTerms: boolean,
) => {
  const translatedFilters = Object.entries(filtersToTranslate).reduce(
    (accumulator: string, [key, value]) => {
      if (value && value.trim() !== "") {
        // Split the value by commas and trim whitespace (user might have entered e.g. 12,17,18)
        const valuesArray = value.split(",").map(v => v.trim()).filter(v => v !== "");
        
        if (valuesArray.length > 0) {
          // Create the CQL string with OR between values
          var orClause = '';
          if (key === 'ages' || key === 'publicationyear') {
            orClause = valuesArray.map(v => `${key} within '${v}'`).join(" OR ");
          } else {
            orClause = valuesArray.map(v => `${key}='${v}'`).join(" OR ");
          }
          // return accumulator + (accumulator ? " AND " : "") + `(${orClause})`;
          // Return string with AND in front and parentheses only if user entered several values
          return accumulator + " AND " + (valuesArray.length > 1 ? `(${orClause})` : orClause);
          // and only if the accumulator has content (i.e. we are not appending an AND at the start of the string here)
          // return accumulator + (accumulator ? " AND " : "") + (valuesArray.length > 1 ? `(${orClause})` : orClause);
        }
      }
      return accumulator;
    },
    ""
  );

  // Only prepend AND to if the user has entered something in a TextInput row (i.e. hasValidRowTerms is true) 
  // and has chosen some options in the filters
  // if (hasValidRowTerms && translatedFilters.trim() !== "") {
  //   return ` AND ${translatedFilters}`;
  // }

  return translatedFilters;
};




// const translateTextInputFiltersToCql = (
//   filtersToTranslate: AdvancedSearchTextInputFilterData,
// ) => {
//   // Build an array from the text input filters instead of several entries in a single object
//   // TODO filtersAsArray skal have en type!
//   const filtersAsArray = Object.entries(filtersToTranslate).map(([key, value]) => ({
//     [key]: value
//   }));

//   const translatedFilters = filtersAsArray.reduce((accumulator, obj) => {
//     const [key, value] = Object.entries(obj)[0];
//     if (value === "") { return "" };
//     // key needs to be cast to be a key from advancedSearchFilters to look op the actual CQL code
//     const condition = `${advancedSearchFilters[key as keyof typeof advancedSearchFilters]} = ${value}`;
//     return `${accumulator} AND ${condition}`;
//   }, "");
//   return translatedFilters;
// };

// export const wrapFiltersInParentheses = (filters: string) => {
//   // No filters, no wrapping needed.
//   if (filters.trim() === "") {
//     return "";
//   }
//   // If there's only one clause, no wrapping is needed either.
//   if (!filters.includes(" OR ")) {
//     return filters;
//   }
//   // The filter string always start with " AND", so we can work with that.
//   const splitFiltersArray = filters.split(" AND", 2);
//   return `${splitFiltersArray.join(" AND (")})`;
// };

export const translateSearchObjectToCql = (
  searchObject: AdvancedSearchQuery
) => {
  const hasValidRowTerms = searchObject.rows.some(row => row.term.trim() !== "");
  const rowsAsCql = translateRowsToCql(searchObject.rows);
  const filtersAsCql = translateFiltersToCql(searchObject.filters);
  const textInputFiltersAsCql = translateTextInputFiltersToCql(searchObject.textInputFilters);

  const onlyFiltersString = `${filtersAsCql}${textInputFiltersAsCql}`;
  
  // Remove the first "AND " if `hasValidRowTerms` is false
  if (!hasValidRowTerms && onlyFiltersString.startsWith(" AND ")) {
    return rowsAsCql + onlyFiltersString.replace(/^ AND /, "");
  }

  return `${rowsAsCql}${filtersAsCql}${textInputFiltersAsCql}`;
};

export const shouldAdvancedSearchButtonBeDisabled = (
  isAdvancedSearch: boolean,
  object: AdvancedSearchQuery,
  cql: string
) => {
  switch (isAdvancedSearch) {
    case true:

      // Allow search if there are valid selections in the MultiSelect dropdowns
      // const hasValidSelections = object.filters.materialTypes.length > 0 || 
      //                            object.filters.fiction.length > 0 || 
      //                            object.filters.accessibility.length > 0;

      // object.filters.materialTypes

      // return !(object && (hasValidSelections || object.rows.some(row => row.term !== "")));
      // return !(object && !!object.rows.find((row) => row.term !== ""));

      // We always want the user to be able to send off the search I should think
      return false;
    default:
      return cql.trim() === "";
  }
};

export const commaSeparatedStringToArray = (input: string): string[] => {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

export default {};
